/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetGroupByAttribute';

const ActionSetGroupByAttributeManager: ActionManagerFn<
  'ActionSetGroupByAttribute'
> = (platform: Platform): ActionManager<'ActionSetGroupByAttribute'> => {
  return {
    ...baseActionManager(platform, 'ActionSetGroupByAttribute'),
    defaultValue,
    props: {
      ...baseActionProps,
      attributeCode: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'QUERY_FILTER',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
    },
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        parent.type === 'ActionQuery' || parent.type === 'TriggerBeforeQuery'
      );
    },
    name: 'Set Group By Attribute',
    getDisplayText: (item) => item.props.attributeCode,
    description: `**Overview**<br/>
The Set Group By Attribute action is a query structuring component designed to organize data into categories or buckets based on shared values. It corresponds directly to the GROUP BY clause in SQL. This action is the essential companion to "Aggregate" operations; it defines how the data should be segmented before calculations (like sums or counts) are performed.<br/><br/>
This component cannot exist independently; it is strictly an architectural element used within ActionQuery or TriggerBeforeQuery. While ActionSetAggregateAttribute tells the system what math to do (e.g., "Sum the Sales"), this action tells the system how to break it down (e.g., "by Region").

**Key Features**<br/>
* **Data Segmentation:** Instructs the database to collapse multiple records with the same value for a specific attribute into a single summary row.
* **Aggregation Prerequisite:** Critical for generating meaningful reports. Without a "Group By," an aggregation returns a single global number (e.g., "Total Sales"). With "Group By," it returns actionable breakdowns (e.g., "Total Sales per Country").
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, ensuring it only functions within valid data querying contexts where query structure is mutable.
* **Type Agnostic:** Capable of grouping by various data types, including categories (Strings), time periods (Dates), or numerical codes.

**Properties**<br/>
* **Attribute (attributeCode) (Required):** A ViewAttributeChooser property.
  * *Function:* The specific field (column) to use for grouping the results.
  * *Example:* Selecting Department will result in one row for "Sales," one for "Marketing," one for "HR," etc.

**Use Cases**<br/>
* **Sales Reports:** Grouping by SalesPerson to calculate the total revenue generated by each individual employee.
* **Status Summaries:** Grouping by OrderStatus to count how many orders are currently "Pending," "Shipped," or "Delivered."
* **Time-Series Data:** Grouping by Year or Month to visualize trends over time (e.g., "New Users per Month").`,
  };
};

export default ActionSetGroupByAttributeManager;
